import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { premiumContentControlsStyles } from "./premium-content-controls.css";

import "../expandible-switch/expandible-switch";
import "../form-elements/input/wow-input";

/**
 * Premium Content Controls Component
 * Handles premium subscription and one-time purchase settings as separate optional features
 *
 * Features:
 * - Expandible switch for premium subscription (requires subscription to access)
 * - Separate expandible switch for one-time purchase (allows buying without subscription)
 * - Both options are independent and optional
 * - Consistent with form pattern (CTA, discount, brand, FAQ sections)
 *
 * Usage:
 * <premium-content-controls
 *   .contentType=${"post"}
 *   .currentTier=${"free"}
 *   .hasTier=${false}
 *   .oneTimePurchaseEnabled=${false}
 *   @tier-change=${handleTierChange}
 *   @one-time-purchase-change=${handleOneTimePurchaseChange}
 * ></premium-content-controls>
 */
@customElement("premium-content-controls")
export class PremiumContentControls extends LitElement {
  static override styles = premiumContentControlsStyles;

  @property({ type: String })
  contentType: "post" | "link" = "post";

  @property({ type: String })
  currentTier: "free" | "premium" = "free";

  @property({ type: Boolean })
  hasTier = false;

  @property({ type: Boolean })
  oneTimePurchaseEnabled = false;

  @property({ type: Number })
  oneTimePriceCents?: number;

  @property({ type: Boolean })
  hasSubscriptionFlag = true;

  @property({ type: Boolean })
  hasOneTimePurchaseFlag = true;

  @state()
  private isPremium = false;

  @state()
  private oneTimePrice = "";

  override connectedCallback() {
    super.connectedCallback();
    this.isPremium = this.currentTier === "premium";
    this.updatePriceFromCents();
  }

  override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("currentTier")) {
      this.isPremium = this.currentTier === "premium";
    }
    if (changedProperties.has("oneTimePriceCents")) {
      this.updatePriceFromCents();
    }
  }

  private updatePriceFromCents() {
    if (this.oneTimePriceCents && this.oneTimePriceCents > 0) {
      this.oneTimePrice = (this.oneTimePriceCents / 100).toFixed(2);
    }
  }

  private handlePremiumExpandedChange(e: CustomEvent) {
    const newTier = e.detail.expanded ? "premium" : "free";
    this.isPremium = e.detail.expanded;

    console.log("[PREMIUM CONTROLS] Tier changed to:", newTier, "expanded:", e.detail.expanded);

    this.dispatchEvent(
      new CustomEvent("tier-change", {
        bubbles: true,
        composed: true,
        detail: {
          tier: newTier,
        },
      }),
    );
    console.log("[PREMIUM CONTROLS] tier-change event dispatched");
  }

  private handleOneTimePurchaseExpandedChange(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent("one-time-purchase-change", {
        bubbles: true,
        composed: true,
        detail: {
          enabled: e.detail.expanded,
          priceCents: this.oneTimePrice ? Math.round(parseFloat(this.oneTimePrice) * 100) : undefined,
        },
      }),
    );
  }

  private handleOneTimePriceInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.oneTimePrice = input.value;
  }

  private handleOneTimePriceBlur(e: Event) {
    const input = e.target as HTMLInputElement;
    this.oneTimePrice = input.value;

    this.dispatchEvent(
      new CustomEvent("one-time-purchase-change", {
        bubbles: true,
        composed: true,
        detail: {
          enabled: true,
          priceCents: this.oneTimePrice ? Math.round(parseFloat(this.oneTimePrice) * 100) : undefined,
        },
      }),
    );
  }

  override render() {
    return html`
      ${this.hasSubscriptionFlag
        ? html`
            <expandible-switch
              ?isExpanded=${this.isPremium}
              @expandedChange=${this.handlePremiumExpandedChange}
              label="Premium Subscription"
              help-text="Require subscription to access this content"
            >
            </expandible-switch>
          `
        : nothing}
      ${this.hasOneTimePurchaseFlag
        ? html`
            <expandible-switch
              ?isExpanded=${this.oneTimePurchaseEnabled}
              @expandedChange=${this.handleOneTimePurchaseExpandedChange}
              label="One-Time Purchase"
              help-text="Allow fans to buy this content"
            >
              <div slot="content" class="form-field">
                <wow-input
                  type="number"
                  inputmode="decimal"
                  label="One-Time Price (€)"
                  help-text="Price in euros (e.g., 2.99)"
                  placeholder="2.99"
                  min="1"
                  max="999"
                  step="0.01"
                  required
                  .value=${this.oneTimePrice}
                  @input=${this.handleOneTimePriceInput}
                  @blur=${this.handleOneTimePriceBlur}
                >
                </wow-input>
              </div>
            </expandible-switch>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "premium-content-controls": PremiumContentControls;
  }
}
